import { useMemo, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import api from "../api/axios";
import { getIssueById, reassignIssue, updateIssueStatus, deleteIssue, moveToSprint } from "../api/issueApi";
import {
  createDailyNotebookEntry,
  deleteDailyNotebookEntry,
  fetchDailyNotebookEntries,
  updateDailyNotebookEntry,
  toggleDailyNotebookReaction,
} from "../api/dailyNotebookApi";
import { toDateInputValue, parseCalendarDate, calendarDateIso } from "../utils/dateOnly";
import TaskDetailModal from "./TaskDetailModal";
import { IssueDetailModal } from "../pages/Issues";
import { DEFAULT_DEPARTMENT } from "../constants/departments";
import MentionComposer from "./MentionComposer";
import { lockAppScroll } from "../utils/lockAppScroll";
import {
  splitTextWithMentions,
  planNotebookEntry,
  stripNotebookPrefix,
} from "../utils/dailyNotebookMentions";
import {
  accentBorderColor,
  entryAccentKey,
  entryMatchesFilters,
  etaDaysBetween,
  countOpenNotebookTasks,
  NOTEBOOK_STATUS_FILTERS,
  buildNotebookNavIds,
  patchNotebookStoryChildrenDone,
  patchNotebookEntrySprint,
  isTaskClosed,
  groupSubtasks,
} from "../utils/dailyNotebookUtils";

// Same palette/hash used by EmployeeProfile.js and UserManagement.js, so a
// person's avatar color is consistent across the app.
const AVATAR_COLORS = ["#1272B3", "#0891b2", "#059669", "#7c3aed", "#db2777", "#d97706", "#dc2626"];
function avatarInitial(name) {
  return (name || "?").trim().charAt(0).toUpperCase() || "?";
}
function avatarColor(name) {
  let h = 0;
  for (const c of name || "") h = (h * 31 + c.charCodeAt(0)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[h];
}

/** Raw signed day count to a due date — for the compact ETA chip's color/label. */
function etaDayDiff(dueDateIso) {
  if (!dueDateIso) return null;
  return parseCalendarDate(dueDateIso).startOf("day").diff(dayjs().startOf("day"), "day");
}

function formatDisplayDate(dateStr) {
  const iso = calendarDateIso(dateStr);
  if (!iso) return "";
  const todayIso = dayjs().format("YYYY-MM-DD");
  const yesterdayIso = dayjs().subtract(1, "day").format("YYYY-MM-DD");
  if (iso === todayIso) return "Today";
  if (iso === yesterdayIso) return "Yesterday";
  return parseCalendarDate(iso).format("ddd, MMM D, YYYY");
}

function patchNotebookTaskStatus(cached, issueId, status) {
  if (!cached?.entries) return cached;
  return {
    ...cached,
    entries: cached.entries.map((entry) => ({
      ...entry,
      tasks: (entry.tasks || []).map((t) =>
        String(t.issue_id) === String(issueId) ? { ...t, status } : t
      ),
      subtasks: (entry.subtasks || []).map((t) =>
        String(t.issue_id) === String(issueId) ? { ...t, status } : t
      ),
    })),
  };
}

function patchNotebookTaskDueDate(cached, issueId, dueDate) {
  if (!cached?.entries) return cached;
  const dueIso = dueDate ? String(dueDate).slice(0, 10) : null;
  return {
    ...cached,
    entries: cached.entries.map((entry) => {
      const tasks = entry.tasks || [];
      const subtasks = entry.subtasks || [];
      const isHeadingTask = tasks.some((t) => String(t.issue_id) === String(issueId));
      const isSubtask = subtasks.some((t) => String(t.issue_id) === String(issueId));
      if (!isHeadingTask && !isSubtask) return entry;
      const withDue = (t) =>
        String(t.issue_id) === String(issueId) ? { ...t, due_date: dueIso } : t;
      return {
        ...entry,
        // The line's own ETA follows its heading tasks, not one bullet's date.
        due_date: isHeadingTask ? dueIso ?? entry.due_date : entry.due_date,
        tasks: tasks.map(withDue),
        subtasks: subtasks.map(withDue),
      };
    }),
  };
}

/** The fixed set the picker offers — mirrors ALLOWED_REACTIONS on the server. */
const REACTION_CHOICES = ["👍", "✅", "👀", "🎉", "❓"];

/**
 * Reaction row: existing reactions as toggle chips, plus a small picker.
 *
 * Deliberately a fixed set with no free picker and no comment threads — in a
 * standup the question is only "seen / done / blocked?", and anything richer
 * turns the notebook back into a discussion thread.
 */
function ReactionRow({ reactions, onToggle, disabled }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const stop = (e) => e.stopPropagation();
  const existing = reactions || [];
  const used = new Set(existing.map((r) => r.emoji));
  const unused = REACTION_CHOICES.filter((e) => !used.has(e));

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mt: 0.35 }}
      onClick={stop}
    >
      {existing.map((r) => (
        <Box
          component="button"
          type="button"
          key={r.emoji}
          disabled={disabled}
          onClick={(e) => {
            stop(e);
            onToggle(r.emoji);
          }}
          title={r.reacted ? "Remove your reaction" : "React"}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.35,
            px: 0.6,
            py: 0.1,
            borderRadius: 999,
            cursor: disabled ? "default" : "pointer",
            fontSize: "0.72rem",
            lineHeight: 1.4,
            border: "1px solid",
            borderColor: r.reacted ? "#93c5fd" : "#e7e5e4",
            bgcolor: r.reacted ? "#eff6ff" : "#fafaf9",
            color: r.reacted ? "#1d4ed8" : "#57534e",
            fontWeight: r.reacted ? 700 : 600,
          }}
        >
          <span>{r.emoji}</span>
          <span>{r.count}</span>
        </Box>
      ))}

      {pickerOpen
        ? unused.map((emoji) => (
            <Box
              component="button"
              type="button"
              key={emoji}
              disabled={disabled}
              onClick={(e) => {
                stop(e);
                onToggle(emoji);
                setPickerOpen(false);
              }}
              sx={{
                px: 0.5,
                py: 0.1,
                borderRadius: 999,
                border: "1px solid #e7e5e4",
                bgcolor: "#fff",
                cursor: "pointer",
                fontSize: "0.78rem",
                lineHeight: 1.4,
              }}
            >
              {emoji}
            </Box>
          ))
        : null}

      {unused.length > 0 && (
        <Box
          component="button"
          type="button"
          aria-label={pickerOpen ? "Close reactions" : "Add a reaction"}
          onClick={(e) => {
            stop(e);
            setPickerOpen((v) => !v);
          }}
          sx={{
            px: 0.55,
            py: 0.1,
            borderRadius: 999,
            border: "1px dashed #d6d3d1",
            bgcolor: "transparent",
            cursor: "pointer",
            fontSize: "0.68rem",
            lineHeight: 1.4,
            color: "#a8a29e",
            "&:hover": { borderColor: "#a8a29e", color: "#57534e" },
          }}
        >
          {pickerOpen ? "×" : "☺ +"}
        </Box>
      )}
    </Box>
  );
}

/** Small color-coded chip leading with urgency ("1d over") instead of a full sentence. */
function EtaChip({ dueDateIso, isDone, isNA }) {
  if (!dueDateIso) return null;
  // N/A is closed (isTaskClosed treats Won't Fix as closed, same as a real
  // completion) but is not a completion — showing "done" here would say the
  // opposite of what the status dropdown right next to it already says.
  if (isNA) return null;
  if (isDone) {
    return (
      <Typography
        component="span"
        sx={{
          fontSize: "0.66rem",
          fontWeight: 700,
          px: 0.6,
          py: 0.1,
          borderRadius: 1,
          bgcolor: "#dcfce7",
          color: "#15803d",
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        done
      </Typography>
    );
  }
  const diff = etaDayDiff(dueDateIso);
  if (diff == null) return null;
  const label = diff < 0 ? `${Math.abs(diff)}d over` : diff === 0 ? "today" : `in ${diff}d`;
  const bg = diff < 0 ? "#fbe9e7" : diff === 0 ? "#fbf1dc" : "#f5f5f4";
  const color = diff < 0 ? "#c0392b" : diff === 0 ? "#b8860b" : "#78716c";
  return (
    <Typography
      component="span"
      sx={{
        fontSize: "0.66rem",
        fontWeight: 700,
        px: 0.6,
        py: 0.1,
        borderRadius: 1,
        bgcolor: bg,
        color,
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
  );
}

/** Compact single-line row for a note with exactly one assignee. Tick closes/reopens
 *  the task directly — the same PUT /issues/:id/status the old dropdown always used. */
/**
 * Real backend status values a task row's dropdown offers, in the order they
 * progress — plus the two special actions Move to backlog / Mark N/A ride
 * alongside, so both are reachable without a separate "..." menu.
 */
const TASK_STATE_OPTIONS = [
  { value: "Open", label: "Open", bg: "#f5f5f4", color: "#57534e" },
  { value: "In Progress", label: "In Progress", bg: "#dbeafe", color: "#1e40af" },
  { value: "Testing", label: "In Review", bg: "#ede9fe", color: "#6d28d9" },
  { value: "Blocked", label: "Blocked", bg: "#fee2e2", color: "#b91c1c" },
  { value: "Closed", label: "Done", bg: "#dcfce7", color: "#15803d" },
];

/** Style for the dropdown's own closed shell — reflects whichever state (real
 *  status, or the two special actions) currently applies to the row. */
function taskStateStyle(status) {
  if (status === "Won't Fix") return { bg: "#f5f5f4", color: "#78716c" };
  const match = TASK_STATE_OPTIONS.find((o) => o.value === status);
  if (match) return { bg: match.bg, color: match.color };
  // Any other closed value (Fixed, Duplicate, Invalid, ...) reads as Done.
  if (isTaskClosed(status)) return { bg: "#dcfce7", color: "#15803d" };
  return { bg: "#f5f5f4", color: "#57534e" };
}

/**
 * One task's state, in one control. A real status moves it through the same
 * pipeline the checkbox always did; Backlog and Mark N/A are the two actions
 * that used to live behind a separate "..." menu — folded in here so a
 * single dropdown covers all of it, colored by whatever the row's current
 * state actually is.
 *
 * canMoveOrMarkNA/onMoveToBacklog/onMarkNA are only present on a row backed
 * by a Story (every tagged line except a handful predating that shape) —
 * the two options are simply absent otherwise, same as the menu they replace.
 */
function TaskStateSelect({
  task,
  onStatusChange,
  isUpdating,
  canMoveOrMarkNA,
  onMoveToBacklog,
  onMarkNA,
  isBacklog,
  isNA,
}) {
  const stop = (e) => e.stopPropagation();
  // The Select's value must resolve to one of its own options or MUI shows
  // it blank — normalize anything else (Reopened, or a closed value this
  // list doesn't spell out like Fixed/Duplicate) to the nearest option that
  // is actually offered, so every real status the backend can produce still
  // shows something sensible here.
  const knownValue = TASK_STATE_OPTIONS.some((o) => o.value === task.status);
  const current = isNA ? "__NA__" : knownValue ? task.status : isTaskClosed(task.status) ? "Closed" : "Open";
  const style = isNA ? taskStateStyle("Won't Fix") : taskStateStyle(task.status);

  const handleChange = (e) => {
    const next = e.target.value;
    if (isUpdating) return;
    if (next === "__BACKLOG__") return onMoveToBacklog?.();
    if (next === "__NA__") return onMarkNA?.();
    onStatusChange(task.issue_id, next);
  };

  return (
    <Select
      size="small"
      value={current}
      onChange={handleChange}
      onClick={stop}
      onMouseDown={stop}
      disabled={isUpdating}
      renderValue={(v) => {
        const opt = TASK_STATE_OPTIONS.find((o) => o.value === v);
        if (v === "__NA__") return "N/A";
        return opt?.label || v;
      }}
      sx={{
        minWidth: 96,
        height: 26,
        fontSize: "0.7rem",
        fontWeight: 700,
        borderRadius: "6px",
        ".MuiOutlinedInput-notchedOutline": { border: "none" },
        // The colored pill is the select's own visible box — a bare
        // top-level bgcolor/color on <Select> is overridden by this inner
        // element's own background, which is why every state rendered the
        // same pale grey regardless of TASK_STATE_OPTIONS's per-state color.
        ".MuiSelect-select": {
          py: 0.3,
          pl: 0.9,
          pr: "22px !important",
          bgcolor: style.bg,
          color: style.color,
          borderRadius: "6px",
        },
        ".MuiSelect-icon": { color: style.color },
        opacity: isUpdating ? 0.6 : 1,
      }}
    >
      {TASK_STATE_OPTIONS.map((o) => (
        <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.8rem" }}>
          {o.label}
        </MenuItem>
      ))}
      {canMoveOrMarkNA && !isBacklog && (
        <MenuItem value="__BACKLOG__" sx={{ fontSize: "0.8rem", gap: 1 }}>
          <InventoryOutlinedIcon sx={{ fontSize: 16 }} /> Move to backlog
        </MenuItem>
      )}
      {canMoveOrMarkNA && !isNA && (
        <MenuItem value="__NA__" sx={{ fontSize: "0.8rem", gap: 1, color: "#78716c" }}>
          <BlockOutlinedIcon sx={{ fontSize: 16 }} /> Mark N/A
        </MenuItem>
      )}
    </Select>
  );
}

function SingleTaskRow({
  task,
  onStatusChange,
  onOpenTask,
  isUpdating,
  canMoveOrMarkNA,
  onMoveToBacklog,
  onMarkNA,
  isBacklog,
  isNA,
}) {
  const isDone = isTaskClosed(task.status);

  const stop = (e) => e.stopPropagation();

  const handleToggle = (e) => {
    stop(e);
    if (isUpdating) return;
    onStatusChange(task.issue_id, isDone ? "Reopened" : "Closed");
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, flexShrink: 0 }}>
      <EtaChip dueDateIso={task.due_date} isDone={isDone} isNA={isNA} />
      {/* Covers every state, including the two actions that used to live
          behind a separate "..." menu. */}
      <TaskStateSelect
        task={task}
        onStatusChange={onStatusChange}
        isUpdating={isUpdating}
        canMoveOrMarkNA={canMoveOrMarkNA}
        onMoveToBacklog={onMoveToBacklog}
        onMarkNA={onMarkNA}
        isBacklog={isBacklog}
        isNA={isNA}
      />
      {/* The fast path for the one thing done constantly — tick it and move
          on, no menu required. Green/checked is reserved for an actual
          completion — N/A is closed too (isTaskClosed treats Won't Fix the
          same way, so the toggle-to-reopen behavior below still works for
          it), but showing it checked here would say "done" right next to a
          dropdown that says "N/A", the opposite of what changed. */}
      <IconButton
        size="small"
        role="checkbox"
        aria-checked={isDone && !isNA}
        aria-label={isDone ? "Mark task as not done" : "Mark task as done"}
        disabled={isUpdating}
        onClick={handleToggle}
        onMouseDown={stop}
        onPointerDown={stop}
        sx={{
          width: 22,
          height: 22,
          borderRadius: "6px",
          border: "2px solid",
          borderColor: isDone && !isNA ? "#16a34a" : "#d6d3d1",
          bgcolor: isDone && !isNA ? "#16a34a" : "#fff",
          color: "#fff",
          fontSize: "0.7rem",
          fontWeight: 800,
          opacity: isUpdating ? 0.5 : 1,
          "&:hover": { bgcolor: isDone && !isNA ? "#15803d" : "#f5f5f4" },
        }}
      >
        {isDone && !isNA ? "✓" : ""}
      </IconButton>
    </Box>
  );
}

/** Compact avatar stack for a story/multi-assignee note — click opens the existing
 *  per-person checklist in TaskDetailModal rather than ticking anyone directly here. */
function TaskAvatarStack({ tasks, onOpenTask, storyId }) {
  const stop = (e) => e.stopPropagation();
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={(e) => {
        stop(e);
        onOpenTask(storyId ?? tasks[0]?.issue_id);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          stop(e);
          onOpenTask(storyId ?? tasks[0]?.issue_id);
        }
      }}
      title={tasks.map((t) => t.assignee_name || "Unassigned").join(", ")}
      sx={{
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        flexShrink: 0,
        "&:hover .avatar-item": { transform: "translateY(-1px)" },
      }}
    >
      {tasks.map((t, i) => (
        <Box
          key={t.issue_id}
          className="avatar-item"
          sx={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            bgcolor: avatarColor(t.assignee_name),
            color: "#fff",
            fontSize: "0.62rem",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px solid #fffef7",
            ml: i === 0 ? 0 : "-8px",
            transition: "transform 0.1s ease",
          }}
        >
          {avatarInitial(t.assignee_name)}
        </Box>
      ))}
    </Box>
  );
}

/** One row in the "By person" view — same tick + ETA chip as a single-assignee
 *  note row, just under a person header instead of a date band. Read-only
 *  reshaping of the same entries; ticking reuses the identical status mutation. */
function PersonTaskRow({
  entry,
  task,
  subs = [],
  statusUpdatingId,
  onStatusChange,
  onOpenTask,
  isUpdating,
  canManageTasks,
  inSprintContext,
  onMoveToBacklog,
  onMarkNA,
}) {
  // `task` is this person's own heading task. It is null for someone who is
  // only named on a bullet - their row is then just the line's text, headed
  // so the bullet has context, with no state controls of its own.
  const isDone = task ? isTaskClosed(task.status) : false;
  // The line's own text, exactly as it was written. NOT task.title: the title
  // is a one-line, whitespace-collapsed rendering for issue cards, so a line
  // typed with line breaks ran together into one paragraph here. (Tasks added
  // straight to the Story are no longer rows of this kind - they are
  // sub-tasks, with titles of their own.)
  const parts = splitTextWithMentions(
    stripNotebookPrefix(entry.body_text || (task ? task.title : "") || "")
  );
  const storyId = entry.story_id ? Number(entry.story_id) : null;
  const canMoveOrMarkNA = Boolean(task) && canManageTasks && inSprintContext && storyId != null;
  const isBacklog = inSprintContext && entry.sprint_id == null;
  const isNA = task ? task.status === "Won't Fix" : false;
  const openTarget = storyId || task?.issue_id;
  const subGroups = groupSubtasks(subs);

  return (
    <Box
      sx={{
        "&:not(:last-child)": {
          borderBottom: "1px solid",
          borderBottomColor: "rgba(120,113,108,0.12)",
        },
        pb: subGroups.length > 0 ? 0.4 : 0,
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => onOpenTask(openTarget)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpenTask(openTarget);
          }
        }}
        sx={{
          display: "flex",
          // flex-start rather than center: same reasoning as the date-grouped
          // row - a wrapped multi-line entry shouldn't float the tick control
          // in the vertical middle of the block.
          alignItems: "flex-start",
          gap: 0.75,
          py: 0.3,
          px: 1,
          cursor: "pointer",
          borderRadius: 1,
          "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
        }}
      >
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: "0.85rem",
            lineHeight: 1.4,
            color: isDone ? "#a8a29e" : "#292524",
            textDecoration: isDone ? "line-through" : "none",
            textDecorationColor: "#d6d3d1",
            // Same fix as the date-grouped row: let a long line wrap instead
            // of clipping with an ellipsis.
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
          }}
        >
          {storyId && (
            <Box component="span" sx={{ color: "#7c3aed", fontWeight: 700, fontSize: "0.72rem", mr: 0.5 }}>
              📖
            </Box>
          )}
          {parts.map((p, i) =>
            p.type === "mention" ? (
              <Box
                component="span"
                key={i}
                sx={{ color: "#2C63AE", fontWeight: 700, bgcolor: "rgba(219,234,254,0.7)", px: 0.3, borderRadius: 0.5 }}
              >
                {p.value}
              </Box>
            ) : (
              <span key={i}>{p.value}</span>
            )
          )}
        </Typography>
        {/* Every row here is one person's own child Task, story or not - so it
            ticks the same way. Closing the last open child auto-closes the
            parent Story server-side (updateIssueStatus), which is what makes
            "finish the story from the list" work without a second control.
            mt matches the row's flex-start alignment, keeping the tick level
            with the first text line once a long entry wraps. */}
        {task ? (
          <Box sx={{ mt: "0.15em" }}>
            <SingleTaskRow
              task={task}
              onStatusChange={onStatusChange}
              onOpenTask={onOpenTask}
              isUpdating={isUpdating}
              canMoveOrMarkNA={canMoveOrMarkNA}
              onMoveToBacklog={() => onMoveToBacklog?.(storyId)}
              onMarkNA={() => onMarkNA?.(task.issue_id)}
              isBacklog={isBacklog}
              isNA={isNA}
            />
          </Box>
        ) : null}
      </Box>
      {subGroups.map((group) => (
        <SubtaskGroupRow
          key={group.key}
          group={group}
          storyId={storyId}
          onStatusChange={onStatusChange}
          onOpenTask={onOpenTask}
          statusUpdatingId={statusUpdatingId}
          showOwner={false}
        />
      ))}
    </Box>
  );
}

/**
 * Done checkbox for a line tagging several people.
 *
 * Ticking closes every assignee's task; unticking reopens them. The parent
 * Story follows automatically server-side, so finishing a shared line takes
 * one click instead of opening the modal and ticking each person.
 */
function MultiTaskDoneCheckbox({ tasks, onStatusChange, isUpdating }) {
  const allDone = tasks.length > 0 && tasks.every((t) => isTaskClosed(t.status));
  const stop = (e) => e.stopPropagation();

  const handleToggle = (e) => {
    stop(e);
    if (isUpdating) return;
    const next = allDone ? "Reopened" : "Closed";
    // Only touch the ones that need it, so a partly-done line doesn't
    // pointlessly rewrite tasks already in the target state.
    tasks
      .filter((t) => (allDone ? isTaskClosed(t.status) : !isTaskClosed(t.status)))
      .forEach((t) => onStatusChange(t.issue_id, next));
  };

  const doneCount = tasks.filter((t) => isTaskClosed(t.status)).length;

  return (
    <IconButton
      size="small"
      role="checkbox"
      aria-checked={allDone}
      aria-label={allDone ? "Reopen all tasks on this line" : "Mark all tasks on this line done"}
      title={`${doneCount}/${tasks.length} done`}
      disabled={isUpdating}
      onClick={handleToggle}
      onMouseDown={stop}
      onPointerDown={stop}
      sx={{
        width: 22,
        height: 22,
        borderRadius: "6px",
        border: "2px solid",
        borderColor: allDone ? "#16a34a" : "#d6d3d1",
        bgcolor: allDone ? "#16a34a" : "#fff",
        color: "#fff",
        fontSize: "0.7rem",
        fontWeight: 800,
        flexShrink: 0,
        opacity: isUpdating ? 0.5 : 1,
        "&:hover": { bgcolor: allDone ? "#15803d" : "#f5f5f4" },
      }}
    >
      {allDone ? "✓" : doneCount > 0 ? "–" : ""}
    </IconButton>
  );
}

/**
 * Row overflow menu.
 *
 * Edit and delete are occasional actions — sitting them permanently on every
 * row competed with the tick, which is the one thing used constantly. They
 * live behind ⋯ so the row reads as: what it is, who owns it, is it done.
 */
function NotebookLineMenu({
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  disabled,
  // Steward-only, mirroring the same move-to-backlog control already in
  // TaskDetailModal — this is a shortcut to it, not a new permission. Mark
  // N/A moved to the row's own status dropdown (TaskStateSelect), since it
  // is a per-task action and this menu is for the whole line.
  canMoveToBacklog,
  onMoveToBacklog,
  isBacklog,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const stop = (e) => e.stopPropagation();
  const showMoveToBacklog = canMoveToBacklog && onMoveToBacklog && !isBacklog;
  if (!canEdit && !canDelete && !showMoveToBacklog) return null;

  const close = () => setAnchorEl(null);

  return (
    <>
      <IconButton
        size="small"
        aria-label="More actions"
        aria-haspopup="true"
        disabled={disabled}
        onClick={(e) => {
          stop(e);
          setAnchorEl(e.currentTarget);
        }}
        onMouseDown={stop}
        sx={{ flexShrink: 0, color: "#a8a29e", p: 0.25, "&:hover": { color: "#57534e" } }}
      >
        <MoreHorizIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        onClick={stop}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {canEdit && (
          <MenuItem
            onClick={(e) => {
              stop(e);
              close();
              onEdit(e);
            }}
            sx={{ fontSize: "0.82rem", gap: 1 }}
          >
            <EditOutlinedIcon sx={{ fontSize: 17 }} /> Edit
          </MenuItem>
        )}
        {showMoveToBacklog && (
          <MenuItem
            onClick={(e) => {
              stop(e);
              close();
              onMoveToBacklog(e);
            }}
            sx={{ fontSize: "0.82rem", gap: 1 }}
          >
            <InventoryOutlinedIcon sx={{ fontSize: 17 }} /> Move to backlog
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            onClick={(e) => {
              stop(e);
              close();
              onDelete(e);
            }}
            sx={{ fontSize: "0.82rem", gap: 1, color: "#dc2626" }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 17 }} /> Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

/**
 * One bullet under a notebook heading.
 *
 * A bullet typed in the notebook is one Task per owner, grouped back together
 * here. A single owner gets the same state dropdown + tick as any other row; a
 * bullet shared by several people shows their avatars and a tick that closes
 * everyone's copy, exactly like a multi-person line does. In the "By person"
 * view each group only ever holds that person's own copy, so the owner chip is
 * hidden there (the section header already says who).
 */
function SubtaskGroupRow({
  group,
  storyId,
  onStatusChange,
  onOpenTask,
  statusUpdatingId,
  showOwner = true,
}) {
  const tasks = group.tasks;
  const single = tasks.length === 1 ? tasks[0] : null;
  const isDone = tasks.every((t) => isTaskClosed(t.status));
  const parts = splitTextWithMentions(stripNotebookPrefix(group.title));
  const openTarget = single ? single.issue_id : storyId ?? tasks[0]?.issue_id;
  const isUpdating = tasks.some((t) => statusUpdatingId === t.issue_id);
  const stop = (e) => e.stopPropagation();

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onOpenTask(openTarget)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenTask(openTarget);
        }
      }}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 0.75,
        py: 0.25,
        pr: 0.5,
        // Indented under the heading; nested bullets step in further.
        pl: 3.2 + group.depth * 2.2,
        cursor: "pointer",
        borderRadius: 1,
        "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
      }}
    >
      <Typography
        component="span"
        sx={{ color: "#c4bfba", fontSize: "0.8rem", lineHeight: 1.4, mt: "0.1em", userSelect: "none", flexShrink: 0 }}
      >
        {group.depth > 0 ? "\u21B3" : "\u2013"}
      </Typography>
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: "0.82rem",
          lineHeight: 1.4,
          color: isDone ? "#a8a29e" : "#44403c",
          textDecoration: isDone ? "line-through" : "none",
          textDecorationColor: "#d6d3d1",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        {parts.map((p, i) =>
          p.type === "mention" ? (
            <Box
              component="span"
              key={i}
              sx={{ color: "#2C63AE", fontWeight: 700, bgcolor: "rgba(219,234,254,0.7)", px: 0.3, borderRadius: 0.5 }}
            >
              {p.value}
            </Box>
          ) : (
            <span key={i}>{p.value}</span>
          )
        )}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, flexShrink: 0 }} onClick={stop}>
        {single ? (
          <>
            {showOwner && single.assignee_name ? (
              <TaskAvatarStack tasks={tasks} onOpenTask={() => onOpenTask(single.issue_id)} storyId={null} />
            ) : null}
            <SingleTaskRow
              task={single}
              onStatusChange={onStatusChange}
              onOpenTask={onOpenTask}
              isUpdating={isUpdating}
              // Move to backlog is a Story-level action and N/A is offered from
              // the Story modal; neither belongs on a single bullet.
              canMoveOrMarkNA={false}
              isBacklog={false}
              isNA={single.status === "Won't Fix"}
            />
          </>
        ) : (
          <>
            <TaskAvatarStack tasks={tasks} onOpenTask={onOpenTask} storyId={storyId} />
            <MultiTaskDoneCheckbox tasks={tasks} onStatusChange={onStatusChange} isUpdating={isUpdating} />
          </>
        )}
      </Box>
    </Box>
  );
}

function NotebookLine({
  entry,
  department,
  canManageTasks,
  canDelete,
  canEditLine,
  onDeleteEntry,
  onEditEntry,
  onStatusChange,
  onOpenTask,
  onToggleReaction,
  isReactionPending,
  statusUpdatingId,
  isDeletingEntry,
  isSavingEdit,
  onMoveToBacklog,
  onMarkNA,
  inSprintContext,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(entry.body_text || "");

  // Re-sync the draft when the line changes underneath us (e.g. an issue-side
  // title edit arrived), but never while the user is mid-edit.
  useEffect(() => {
    if (!isEditing) setDraft(entry.body_text || "");
  }, [entry.body_text, isEditing]);

  const parts = splitTextWithMentions(stripNotebookPrefix(entry.body_text || ""));
  const tasks = entry.tasks || [];
  const subtaskGroups = groupSubtasks(entry.subtasks);
  const storyId = entry.story_id ? Number(entry.story_id) : null;
  const accent = entryAccentKey(entry);
  // Keyed on how many people are tagged, not on whether a Story exists.
  // Every tagged line spawns a Story now, so an earlier `!storyId` guard here
  // silently hid the tick on the commonest row of all: one assignee.
  const singleTask = tasks.length === 1 ? tasks[0] : null;
  const openStoryOnLineClick = Boolean(storyId);
  // Whole-line "done" styling (strike-through) only makes sense for a single
  // task — a multi-assignee line has mixed per-person state, shown via avatars.
  const isDone = singleTask ? isTaskClosed(singleTask.status) : false;

  const stopLineClick = (event) => {
    event.stopPropagation();
  };

  const startEditing = (event) => {
    stopLineClick(event);
    setDraft(entry.body_text || "");
    setIsEditing(true);
  };

  const commitEdit = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === String(entry.body_text || "").trim()) {
      setIsEditing(false);
      return;
    }
    onEditEntry(entry, trimmed);
    setIsEditing(false);
  };

  // Backlog acts on the Story (the whole line); it's the one action that
  // still belongs on a summary row even for a multi-assignee line, where
  // there is no single task to hang a per-person dropdown off of. Mark N/A
  // is per-task now (see TaskStateSelect on the row's own dropdown for a
  // single-assignee line) — a multi-assignee line has no one task to apply
  // it to from here, so the menu no longer offers it. A handful of legacy
  // entries predate every tagged line getting a Story, so there is nothing
  // to act on for those. Both are meaningless outside a sprint context
  // (Daily Notebook's own page never has a sprint at all, so every entry
  // there would otherwise show as "backlog").
  const canMoveToBacklog = canManageTasks && inSprintContext && entry.story_id != null;
  const isBacklog = inSprintContext && entry.sprint_id == null;
  const isNA = singleTask?.status === "Won't Fix";
  const overflowMenu = (
    <NotebookLineMenu
      canEdit={canEditLine}
      canDelete={canDelete}
      disabled={isDeletingEntry || isSavingEdit}
      onEdit={startEditing}
      onDelete={(event) => {
        stopLineClick(event);
        onDeleteEntry(entry);
      }}
      canMoveToBacklog={canMoveToBacklog}
      isBacklog={isBacklog}
      onMoveToBacklog={(event) => {
        stopLineClick(event);
        onMoveToBacklog(entry.story_id);
      }}
    />
  );

  const storyChip = storyId ? (
    <Box
      role="button"
      tabIndex={0}
      onClick={(e) => {
        stopLineClick(e);
        onOpenTask(storyId);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpenTask(storyId);
        }
      }}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.35,
        px: 0.6,
        py: 0.1,
        borderRadius: 999,
        border: "1px solid #c7d2fe",
        bgcolor: "#eef2ff",
        color: "#4338ca",
        fontSize: "0.64rem",
        fontWeight: 700,
        cursor: "pointer",
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        flexShrink: 0,
        "&:hover": { bgcolor: "#e0e7ff" },
      }}
    >
      📖 #{storyId}
    </Box>
  ) : null;

  // Multi-assignee lines (a story, or several people tagged with no story
  // parent) keep the row-level click opening the modal — per-person ticking
  // happens inside the Story checklist we already built there. A single task
  // gets a tick right on this row instead.
  const isMultiAssignee = tasks.length > 1;
  const rowOpensModal = Boolean(singleTask) || openStoryOnLineClick || (isMultiAssignee && !storyId);
  const rowOpenTarget = () => {
    // One assignee: their Task is what the row is really about, so open that.
    // The 📖 chip still opens the parent Story for anyone who wants it.
    if (singleTask) return singleTask.issue_id;
    if (storyId) return storyId;
    if (isMultiAssignee) return tasks[0].issue_id;
    return null;
  };

  const lineRow = (
    <Box
      role={rowOpensModal ? "button" : undefined}
      tabIndex={rowOpensModal ? 0 : undefined}
      onClick={rowOpensModal ? () => onOpenTask(rowOpenTarget()) : undefined}
      onKeyDown={
        rowOpensModal
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onOpenTask(rowOpenTarget());
              }
            }
          : undefined
      }
      sx={{
        display: "flex",
        gap: 0.75,
        // flex-start rather than center: once a long line wraps to several
        // rows, centering would float the bullet and the right-rail icons
        // in the vertical middle of the block instead of by the first line.
        // A one-line row looks the same either way.
        alignItems: "flex-start",
        py: isEditing ? 0.45 : 0.3,
        px: 0.25,
        borderLeft: `5px solid ${accentBorderColor(accent)}`,
        pl: 1.1,
        minHeight: 0,
        cursor: rowOpensModal ? "pointer" : "default",
        borderRadius: rowOpensModal ? 1 : 0,
        transition: "background-color 0.15s ease",
        "&:hover": rowOpensModal ? { bgcolor: "rgba(0,0,0,0.03)" } : {},
        // With sub-tasks beneath, the divider belongs to the whole block
        // (the wrapper below), not between the heading and its own bullets.
        ...(subtaskGroups.length === 0
          ? {
              "&:not(:last-child)": {
                borderBottom: "1px solid",
                borderBottomColor: "rgba(120,113,108,0.12)",
              },
            }
          : {}),
      }}
    >
      <Typography
        component="span"
        sx={{
          color: "#a8a29e",
          fontWeight: 700,
          fontSize: "0.85rem",
          lineHeight: 1,
          userSelect: "none",
          flexShrink: 0,
          // Nudges the bullet down to sit level with the first line of text
          // now that the row aligns to flex-start instead of center.
          mt: "0.35em",
        }}
      >
        •
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }} onClick={isEditing ? stopLineClick : undefined}>
        {isEditing ? (
          <TextField
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onClick={stopLineClick}
            onBlur={commitEdit}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                commitEdit();
              } else if (event.key === "Escape") {
                event.preventDefault();
                setDraft(entry.body_text || "");
                setIsEditing(false);
              }
            }}
            autoFocus
            multiline
            fullWidth
            size="small"
            disabled={isSavingEdit}
            helperText="Enter to save · Esc to cancel · updates the linked task titles"
            sx={{
              "& .MuiInputBase-input": { fontSize: "0.875rem", lineHeight: 1.4 },
              "& .MuiFormHelperText-root": { fontSize: "0.62rem", ml: 0.25, mt: 0.25 },
            }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: "0.875rem",
              lineHeight: 1.4,
              color: isDone ? "#a8a29e" : "#292524",
              textDecoration: isDone ? "line-through" : "none",
              textDecorationColor: "#d6d3d1",
              // Wraps to as many lines as the text needs — a long task line
              // was being cut off with no way to read it without opening the
              // detail modal. The row grows with it since nothing here forces
              // a fixed height.
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          >
            {parts.map((p, i) =>
              p.type === "mention" ? (
                <Box
                  component="span"
                  key={i}
                  sx={{
                    color: "#2C63AE",
                    fontWeight: 700,
                    bgcolor: "rgba(219,234,254,0.7)",
                    px: 0.3,
                    borderRadius: 0.5,
                  }}
                >
                  {p.value}
                </Box>
              ) : (
                <span key={i}>{p.value}</span>
              )
            )}
          </Typography>
        )}
        {tasks.length === 0 && !isEditing && (
          <Typography variant="caption" sx={{ color: "#a8a29e", display: "block", fontSize: "0.65rem" }}>
            Note only
          </Typography>
        )}
        {!isEditing && onToggleReaction && (
          <ReactionRow
            reactions={entry.reactions}
            disabled={isReactionPending}
            onToggle={(emoji) => onToggleReaction(entry, emoji)}
          />
        )}
      </Box>

      {/* Right rail — a tick for one assignee, an avatar stack for several.
          mt matches the bullet's nudge so it sits level with the first line
          once a long entry wraps, instead of the top edge of a taller row. */}
      {!isEditing && (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 0.6, flexShrink: 0, mt: "0.15em" }}
          onClick={stopLineClick}
        >
          {/* A single-assignee line's own status dropdown already shows
              "N/A" as its selected value, so this badge only adds anything
              for a multi-assignee line, which has no per-task dropdown to
              show it — Move to backlog only, from the "..." menu. */}
          {isBacklog && (
            <Box
              component="span"
              sx={{
                fontSize: "0.62rem",
                fontWeight: 700,
                letterSpacing: "0.03em",
                px: 0.6,
                py: 0.1,
                borderRadius: 999,
                border: "1px solid #fde68a",
                bgcolor: "#fffbeb",
                color: "#92400e",
              }}
            >
              Backlog
            </Box>
          )}
          {storyId && storyChip}
          {isMultiAssignee ? (
            <>
              <TaskAvatarStack tasks={tasks} onOpenTask={onOpenTask} storyId={storyId} />
              {/* Ticking a multi-person line closes everyone's task; the last
                  one closing auto-closes the parent Story server-side. */}
              <MultiTaskDoneCheckbox
                tasks={tasks}
                onStatusChange={onStatusChange}
                isUpdating={tasks.some((t) => statusUpdatingId === t.issue_id)}
              />
            </>
          ) : singleTask ? (
            <SingleTaskRow
              task={singleTask}
              onStatusChange={onStatusChange}
              onOpenTask={onOpenTask}
              isUpdating={statusUpdatingId === singleTask.issue_id}
              canMoveOrMarkNA={canManageTasks && inSprintContext && storyId != null}
              onMoveToBacklog={() => onMoveToBacklog(storyId)}
              onMarkNA={() => onMarkNA(singleTask.issue_id)}
              isBacklog={isBacklog}
              isNA={isNA}
            />
          ) : null}
          {overflowMenu}
        </Box>
      )}
    </Box>
  );

  // No bullets: exactly the row it always was.
  if (subtaskGroups.length === 0) return lineRow;

  return (
    <Box
      sx={{
        "&:not(:last-child)": {
          borderBottom: "1px solid",
          borderBottomColor: "rgba(120,113,108,0.12)",
        },
        pb: 0.4,
      }}
    >
      {lineRow}
      {subtaskGroups.map((group) => (
        <SubtaskGroupRow
          key={group.key}
          group={group}
          storyId={storyId}
          onStatusChange={onStatusChange}
          onOpenTask={onOpenTask}
          statusUpdatingId={statusUpdatingId}
        />
      ))}
    </Box>
  );
}

export default function DailyNotebookModal({
  open,
  onClose,
  projectId,
  sprintId = null,
  projectName = "",
  sprintName = "",
  canEdit = false,
  canManageTasks = false,
  canDelete = false,
  department = DEFAULT_DEPARTMENT,
  // The sprint this notebook is scoped to has already shipped (Released or
  // Closed) — new entries against it are refused server-side anyway; this
  // just explains why up front instead of letting the "Add" click fail.
  sprintClosed = false,
}) {
  const qc = useQueryClient();
  const [bodyText, setBodyText] = useState("");
  const [logDate, setLogDate] = useState(() => dayjs());
  const [dueDate, setDueDate] = useState(() => dayjs());
  const [statusFilter, setStatusFilter] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState(null);
  const [filterDateTo, setFilterDateTo] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [selectedIssueId, setSelectedIssueId] = useState(null);
  /** Snapshot of notebook nav ids when detail opens — stable while prev/next navigating. */
  const [modalNavIds, setModalNavIds] = useState(null);
  const [deletingEntryId, setDeletingEntryId] = useState(null);
  const [editingEntryId, setEditingEntryId] = useState(null);
  /** What the list is grouped by. "all" (default) groups every assignee's
   *  open items for standup — that is the more common reason to open this.
   *  "notes" is the flat day-grouped list, one click away; a member id
   *  narrows either view to just that person. */
  const [personFilter, setPersonFilter] = useState("all");

  const entriesQueryKey = ["daily-notebook", projectId, sprintId ?? "project"];

  const invalidateNotebookQueries = () => {
    qc.invalidateQueries({ queryKey: entriesQueryKey });
    qc.invalidateQueries({ queryKey: ["my-issues"] });
    qc.invalidateQueries({ queryKey: ["board"] });
  };

  const { data: selectedIssue } = useQuery({
    queryKey: ["issue", selectedIssueId],
    queryFn: () => getIssueById(selectedIssueId).then((r) => r.data),
    enabled: open && !!selectedIssueId,
  });

  useEffect(() => {
    if (!open) {
      setSelectedIssueId(null);
      setModalNavIds(null);
    }
    if (open) {
      const today = dayjs();
      setLogDate(today);
      setDueDate(today);
    }
  }, [open]);

  // Lock background scroll (main Layout pane only; MUI Dialog locks body)
  useEffect(() => {
    if (!open) return undefined;
    return lockAppScroll();
  }, [open]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: entriesQueryKey,
    queryFn: () =>
      fetchDailyNotebookEntries(projectId, {
        sprintId: sprintId ?? undefined,
        from: dayjs().subtract(45, "day").format("YYYY-MM-DD"),
      }),
    enabled: open && !!projectId,
  });

  const { data: members = [] } = useQuery({
    queryKey: ["project-members", projectId],
    queryFn: () => api.get(`/projects/${projectId}/members`).then((r) => r.data),
    enabled: open && !!projectId,
  });

  // What the text becomes: a heading with sub-tasks when the first line names
  // someone, otherwise one task with its line breaks kept (see
  // planNotebookEntry). Only the heading's names own a heading entry.
  const entryPlan = useMemo(() => planNotebookEntry(bodyText, members), [bodyText, members]);
  const assigneeIds = entryPlan.assigneeIds;
  const hasAssignees = assigneeIds.length > 0;
  const subtaskCount = entryPlan.items.length;
  const isMultiLineSingle = entryPlan.kind === "single" && entryPlan.bodyText.includes("\n");

  const selectedPresetDays = useMemo(
    () => etaDaysBetween(logDate, dueDate),
    [logDate, dueDate]
  );

  const applyEtaPreset = (days) => {
    setDueDate(logDate.add(days, "day"));
  };

  const groupedEntries = useMemo(() => {
    const entries = data?.entries || [];
    const filterOpts = {
      statusFilter,
      dateFrom: filterDateFrom ? filterDateFrom.format("YYYY-MM-DD") : null,
      dateTo: filterDateTo ? filterDateTo.format("YYYY-MM-DD") : null,
    };
    const groups = {};
    for (const entry of entries) {
      if (!entryMatchesFilters(entry, filterOpts)) continue;
      const key = calendarDateIso(entry.log_date) || entry.log_date;
      if (!groups[key]) groups[key] = [];
      groups[key].push(entry);
    }
    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) => Number(b.id) - Number(a.id));
    }
    return Object.entries(groups).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [data?.entries, statusFilter, filterDateFrom, filterDateTo]);

  const notebookNavIds = useMemo(
    () => buildNotebookNavIds(groupedEntries),
    [groupedEntries]
  );

  // Notebook entries regrouped by assignee instead of by day, for going
  // around the room in standup. Read-only reshaping; every row still uses
  // the same tick/click handlers as the day-grouped list, so no new
  // mutation path exists here.
  const groupedByPerson = useMemo(() => {
    const byPerson = new Map();
    const ensureGroup = (key, name) => {
      if (!byPerson.has(key)) byPerson.set(key, { assigneeId: key, name, rows: [] });
      return byPerson.get(key);
    };
    for (const [, entries] of groupedEntries) {
      for (const entry of entries) {
        const tasks = entry.tasks || [];
        const subtasks = entry.subtasks || [];
        if (tasks.length === 0 && subtasks.length === 0) continue;

        // One row per (line, person): their heading task plus their own copy
        // of every bullet. A person who only owns bullets (named on a single
        // bullet) still gets a row, headed by the line's text, so the bullet
        // is not orphaned.
        const rowsForEntry = new Map();
        const rowFor = (key, name, task) => {
          const mapKey = `${key}`;
          if (!rowsForEntry.has(mapKey)) {
            const row = { entry, task: task || null, subs: [] };
            rowsForEntry.set(mapKey, row);
            ensureGroup(key, name).rows.push(row);
          }
          return rowsForEntry.get(mapKey);
        };
        for (const task of tasks) {
          const key = task.assignee_id == null ? "unassigned" : String(task.assignee_id);
          rowFor(key, task.assignee_name || "Unassigned", task);
        }
        for (const sub of subtasks) {
          const key = sub.assignee_id == null ? "unassigned" : String(sub.assignee_id);
          rowFor(key, sub.assignee_name || "Unassigned", null).subs.push(sub);
        }
      }
    }
    const groups = Array.from(byPerson.values());
    const unassigned = groups.find((g) => g.assigneeId === "unassigned");
    const people = groups
      .filter((g) => g.assigneeId !== "unassigned")
      .sort((a, b) => a.name.localeCompare(b.name));
    const ordered = unassigned ? [...people, unassigned] : people;
    for (const g of ordered) {
      g.rows.sort((a, b) => Number(b.entry.id) - Number(a.entry.id));
      const own = g.rows.flatMap((r) => (r.task ? [r.task, ...r.subs] : r.subs));
      g.totalCount = own.length;
      g.openCount = own.filter((t) => !isTaskClosed(t.status)).length;
    }
    return ordered;
  }, [groupedEntries]);

  /** The dropdown's option list: every project member who can be tagged,
   *  regardless of whether they have a task today (an empty section for
   *  someone with nothing due is informative, not a bug). */
  const personFilterOptions = useMemo(
    () =>
      [...members]
        .map((m) => ({ id: String(m.id), name: m.full_name || m.email }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [members]
  );

  const visiblePersonGroups = useMemo(
    () =>
      personFilter === "all"
        ? groupedByPerson
        : groupedByPerson.filter((g) => g.assigneeId === personFilter),
    [groupedByPerson, personFilter]
  );

  useEffect(() => {
    if (!selectedIssueId) {
      setModalNavIds(null);
      return;
    }
    setModalNavIds((prev) => prev ?? notebookNavIds);
  }, [selectedIssueId, notebookNavIds]);

  const createMutation = useMutation({
    mutationFn: (payload) => createDailyNotebookEntry(projectId, payload),
    onSuccess: () => {
      invalidateNotebookQueries();
      setBodyText("");
      setDueDate(logDate);
      toast.success(
        subtaskCount > 0
          ? `Added with ${subtaskCount} sub-task${subtaskCount > 1 ? "s" : ""}`
          : assigneeIds.length > 1
            ? "Story created with a task per assignee"
            : assigneeIds.length === 1
              ? "Task created"
              : "Added to notebook"
      );
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to add line");
    },
  });

  const editEntryMutation = useMutation({
    mutationFn: ({ entryId, bodyText: nextBody }) =>
      updateDailyNotebookEntry(projectId, entryId, { body_text: nextBody }),
    onMutate: async ({ entryId, bodyText: nextBody }) => {
      await qc.cancelQueries({ queryKey: entriesQueryKey });
      const previous = qc.getQueryData(entriesQueryKey);
      qc.setQueryData(entriesQueryKey, (old) =>
        old?.entries
          ? {
              ...old,
              entries: old.entries.map((e) =>
                Number(e.id) === Number(entryId) ? { ...e, body_text: nextBody } : e
              ),
            }
          : old
      );
      return { previous };
    },
    onSuccess: (res) => {
      invalidateNotebookQueries();
      const n = res?.syncedIssueCount || 0;
      toast.success(n > 0 ? `Line and ${n} linked item${n > 1 ? "s" : ""} updated` : "Line updated");
    },
    onError: (err, _vars, context) => {
      if (context?.previous) qc.setQueryData(entriesQueryKey, context.previous);
      toast.error(err.response?.data?.message || "Failed to update line");
    },
    onSettled: () => setEditingEntryId(null),
  });

  /**
   * Reactions toggle optimistically — the whole point is a one-tap
   * acknowledgement, so waiting on a round-trip would defeat it. Rolls back
   * to the snapshot if the request fails.
   */
  const reactionMutation = useMutation({
    mutationFn: ({ entryId, emoji }) => toggleDailyNotebookReaction(projectId, entryId, emoji),
    onMutate: async ({ entryId, emoji }) => {
      await qc.cancelQueries({ queryKey: entriesQueryKey });
      const previous = qc.getQueryData(entriesQueryKey);
      qc.setQueryData(entriesQueryKey, (old) => {
        if (!old?.entries) return old;
        return {
          ...old,
          entries: old.entries.map((e) => {
            if (Number(e.id) !== Number(entryId)) return e;
            const list = e.reactions || [];
            const hit = list.find((r) => r.emoji === emoji);
            let next;
            if (!hit) {
              next = [...list, { emoji, count: 1, reacted: true }];
            } else if (hit.reacted) {
              // Removing mine — drop the chip entirely if I was the only one.
              next =
                hit.count <= 1
                  ? list.filter((r) => r.emoji !== emoji)
                  : list.map((r) => (r.emoji === emoji ? { ...r, count: r.count - 1, reacted: false } : r));
            } else {
              next = list.map((r) => (r.emoji === emoji ? { ...r, count: r.count + 1, reacted: true } : r));
            }
            return { ...e, reactions: next.sort((a, b) => a.emoji.localeCompare(b.emoji)) };
          }),
        };
      });
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) qc.setQueryData(entriesQueryKey, context.previous);
      toast.error(err.response?.data?.message || "Failed to update reaction");
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: entriesQueryKey });
    },
  });

  const handleToggleReaction = (entry, emoji) => {
    reactionMutation.mutate({ entryId: entry.id, emoji });
  };

  const handleEditEntry = (entry, nextBody) => {
    setEditingEntryId(entry.id);
    editEntryMutation.mutate({ entryId: entry.id, bodyText: nextBody });
  };

  const deleteEntryMutation = useMutation({
    mutationFn: (entryId) => deleteDailyNotebookEntry(projectId, entryId),
    onSuccess: () => {
      invalidateNotebookQueries();
      toast.success("Notebook entry deleted");
    },
    onError: (err) => toast.error(err.response?.data?.message || "Failed to delete entry"),
    onSettled: () => setDeletingEntryId(null),
  });

  const statusMutation = useMutation({
    mutationFn: ({ issueId, status }) => {
      const resolution = status === "Fixed" ? "Fixed" : undefined;
      return updateIssueStatus(issueId, status, resolution);
    },
    onMutate: async ({ issueId, status }) => {
      setStatusUpdatingId(issueId);
      await qc.cancelQueries({ queryKey: entriesQueryKey });
      const previous = qc.getQueryData(entriesQueryKey);
      qc.setQueryData(entriesQueryKey, (old) => patchNotebookTaskStatus(old, issueId, status));
      return { previous };
    },
    onSuccess: (_data, { issueId, status }) => {
      qc.setQueryData(entriesQueryKey, (old) => patchNotebookTaskStatus(old, issueId, status));
      qc.invalidateQueries({ queryKey: entriesQueryKey });
      qc.invalidateQueries({ queryKey: ["my-issues"] });
      qc.invalidateQueries({ queryKey: ["board"] });
      toast.success("Task status updated");
    },
    onError: (err, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(entriesQueryKey, context.previous);
      }
      toast.error(err.response?.data?.message || "Failed to update status");
    },
    onSettled: () => setStatusUpdatingId(null),
  });

  const handleStatusChange = (issueId, status) => {
    statusMutation.mutate({ issueId, status });
  };

  /**
   * Move to backlog acts on the line's Story (entry.story_id) — the backend
   * refuses to backlog a bare Task ("move the parent Story instead"), and
   * every tagged line has one. Mark N/A acts on the individual task instead:
   * it is a real status (Won't Fix, distinct from a plain Done so it reads as
   * dismissed rather than completed), and setting it on just one person's
   * task in a shared line correctly leaves the others untouched — the
   * server's own parent-Story rollup (updateIssue) then reflects whatever
   * mix of statuses the children end up in, same as ticking one person done
   * already does.
   */
  const backlogMutation = useMutation({
    mutationFn: ({ storyId }) => moveToSprint(storyId, null),
    onMutate: async ({ storyId }) => {
      await qc.cancelQueries({ queryKey: entriesQueryKey });
      const previous = qc.getQueryData(entriesQueryKey);
      qc.setQueryData(entriesQueryKey, (old) => patchNotebookEntrySprint(old, storyId, null));
      return { previous };
    },
    onSuccess: () => {
      invalidateNotebookQueries();
      toast.success("Moved to backlog");
    },
    onError: (err, _vars, context) => {
      if (context?.previous) qc.setQueryData(entriesQueryKey, context.previous);
      toast.error(err.response?.data?.message || "Could not move to backlog");
    },
  });

  const markNAMutation = useMutation({
    mutationFn: ({ issueId }) => updateIssueStatus(issueId, "Won't Fix", "Won't Fix"),
    onMutate: async ({ issueId }) => {
      await qc.cancelQueries({ queryKey: entriesQueryKey });
      const previous = qc.getQueryData(entriesQueryKey);
      qc.setQueryData(entriesQueryKey, (old) => patchNotebookTaskStatus(old, issueId, "Won't Fix"));
      return { previous };
    },
    onSuccess: () => {
      invalidateNotebookQueries();
      toast.success("Marked N/A");
    },
    onError: (err, _vars, context) => {
      if (context?.previous) qc.setQueryData(entriesQueryKey, context.previous);
      toast.error(err.response?.data?.message || "Could not mark as N/A");
    },
  });

  const handleMoveToBacklog = (storyId) => {
    backlogMutation.mutate({ storyId });
  };

  const handleMarkNA = (issueId) => {
    markNAMutation.mutate({ issueId });
  };

  const handleOpenTask = (issueId) => {
    setSelectedIssueId(issueId);
  };

  const handleCloseTaskDetail = () => {
    setSelectedIssueId(null);
  };

  const handleTaskDetailUpdated = (savedIssue) => {
    const issueId = savedIssue?.issueid ?? savedIssue?.issue_id ?? selectedIssueId;
    if (issueId && savedIssue?.due_date !== undefined) {
      qc.setQueryData(
        entriesQueryKey,
        (old) => patchNotebookTaskDueDate(old, issueId, toDateInputValue(savedIssue.due_date))
      );
    }
    if (issueId && savedIssue?.status !== undefined) {
      qc.setQueryData(entriesQueryKey, (old) => patchNotebookTaskStatus(old, issueId, savedIssue.status));
    }
    if (savedIssue?.issuetype === "Story" && isTaskClosed(savedIssue.status)) {
      qc.setQueryData(entriesQueryKey, (old) => patchNotebookStoryChildrenDone(old, issueId));
    }
    invalidateNotebookQueries();
    if (selectedIssueId) {
      qc.invalidateQueries({ queryKey: ["issue", selectedIssueId] });
    }
  };

  const handleReassignTask = async (issueId, assigneeId) => {
    await reassignIssue(issueId, assigneeId);
    handleTaskDetailUpdated();
    toast.success("Assignee updated");
  };

  const handleDeleteEntry = (entry) => {
    const taskCount = entry.tasks?.length || 0;
    const linkedNote = entry.story_id
      ? " (including the story and all linked tasks)"
      : taskCount > 0
        ? ` (including ${taskCount} linked task${taskCount > 1 ? "s" : ""})`
        : "";
    if (
      !window.confirm(
        `Delete this notebook line${linkedNote}? This cannot be undone.`
      )
    ) {
      return;
    }
    setDeletingEntryId(entry.id);
    deleteEntryMutation.mutate(entry.id);
  };

  const handleDeleteIssueFromDetail = async (issueId) => {
    await deleteIssue(issueId);
    handleCloseTaskDetail();
    invalidateNotebookQueries();
  };

  const handleAddLine = () => {
    if (!bodyText.trim()) {
      toast.error("Write something first");
      return;
    }
    // An entry without an owner has nobody to follow up with, so @ is required
    // here and in work-item creation alike.
    if (entryPlan.kind === "none") {
      toast.error(
        bodyText.includes("@")
          ? "Could not match @names to project members — pick from the @ list"
          : "Tag who owns this with @"
      );
      return;
    }
    if (hasAssignees && !dueDate) {
      toast.error("Set an ETA for tagged people");
      return;
    }
    createMutation.mutate({
      log_date: logDate.format("YYYY-MM-DD"),
      body_text: entryPlan.bodyText,
      due_date: hasAssignees ? dueDate.format("YYYY-MM-DD") : null,
      assignee_ids: assigneeIds,
      sprint_id: sprintId || undefined,
      // A sub-task that names someone goes to them alone; otherwise the
      // server gives it to everyone tagged on the heading.
      ...(entryPlan.items.length > 0 ? { items: entryPlan.items } : {}),
    });
  };

  const openTaskCount = useMemo(() => {
    const entries = data?.entries || [];
    const filterOpts = {
      statusFilter,
      dateFrom: filterDateFrom ? filterDateFrom.format("YYYY-MM-DD") : null,
      dateTo: filterDateTo ? filterDateTo.format("YYYY-MM-DD") : null,
    };
    const filtered = entries.filter((entry) => entryMatchesFilters(entry, filterOpts));
    return countOpenNotebookTasks(filtered);
  }, [data?.entries, statusFilter, filterDateFrom, filterDateTo]);

  const subtitle = [projectName, sprintName].filter(Boolean).join(" · ");

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      disableEnforceFocus={Boolean(selectedIssueId)}
      disableRestoreFocus={Boolean(selectedIssueId)}
      disableAutoFocus={Boolean(selectedIssueId)}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(15, 23, 42, 0.42)" },
        },
        paper: {
          sx: {
            borderRadius: 3,
            width: { xs: "calc(100% - 24px)", sm: "calc(100% - 48px)" },
            maxWidth: { xs: "calc(100% - 24px)", md: 980 },
            height: { xs: "calc(100% - 24px)", sm: "calc(100% - 48px)" },
            maxHeight: { xs: "calc(100% - 24px)", sm: "calc(100% - 48px)" },
            m: { xs: 1.5, sm: 3 },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            bgcolor: "#f5f5f4",
            boxShadow: "0 24px 48px rgba(0,0,0,0.16)",
          },
        },
      }}
    >
      {/* ── Sticky header ── */}
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 1.25,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          borderBottom: "1px solid #e7e5e4",
          bgcolor: "#fff",
          flexShrink: 0,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" fontWeight={800} sx={{ color: "#1c1917", lineHeight: 1.2 }}>
            Daily Notebook
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{ color: "#78716c", fontWeight: 600, display: "block", mt: 0.25 }}
              noWrap
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          {!isLoading && (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#57534e",
                bgcolor: "#f5f5f4",
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                border: "1px solid #e7e5e4",
              }}
            >
              {openTaskCount} Open
            </Typography>
          )}
          <IconButton
            onClick={onClose}
            aria-label="Close"
            size="small"
            sx={{
              color: "#57534e",
              border: "1px solid #e7e5e4",
              bgcolor: "#fafaf9",
              "&:hover": { bgcolor: "#f5f5f4" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
        {/* ── Sticky filters ── */}
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 1,
            bgcolor: "#fff",
            borderBottom: "1px solid #e7e5e4",
            display: "flex",
            flexWrap: "wrap",
            gap: 1.25,
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {NOTEBOOK_STATUS_FILTERS.map((f) => (
                <MenuItem key={f.value} value={f.value}>
                  {f.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <DatePicker
            label="From"
            value={filterDateFrom}
            onChange={(v) => setFilterDateFrom(v)}
            slotProps={{
              textField: { size: "small", sx: { width: 140 } },
            }}
          />
          <DatePicker
            label="To"
            value={filterDateTo}
            onChange={(v) => setFilterDateTo(v)}
            slotProps={{
              textField: { size: "small", sx: { width: 140 } },
            }}
          />
          {(statusFilter !== "all" || filterDateFrom || filterDateTo) && (
            <Button
              size="small"
              onClick={() => {
                setStatusFilter("all");
                setFilterDateFrom(null);
                setFilterDateTo(null);
              }}
              sx={{ textTransform: "none" }}
            >
              Clear filters
            </Button>
          )}

          {/* Grouped by person, for going around the room in standup —
              default "All members" shows everyone's section; picking a
              name narrows the same list down to just theirs. */}
          <FormControl size="small" sx={{ minWidth: 170, ml: "auto" }}>
            <InputLabel>Team member</InputLabel>
            <Select
              label="Team member"
              value={personFilter}
              onChange={(e) => setPersonFilter(e.target.value)}
            >
              <MenuItem value="all">All members</MenuItem>
              <MenuItem value="notes" sx={{ borderBottom: "1px solid #e7e5e4" }}>
                All notes (by date)
              </MenuItem>
              {personFilterOptions.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* ── Scrollable notebook body ── */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            overscrollBehavior: "contain",
            bgcolor: "#fffef7",
            backgroundImage:
              "repeating-linear-gradient(transparent, transparent 27px, rgba(214,211,209,0.28) 28px)",
          }}
        >
          <Box
            sx={{
              maxWidth: 960,
              mx: "auto",
              px: { xs: 2, md: 3 },
              py: 2,
              width: "100%",
            }}
          >
            {isLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Could not load notebook.{" "}
                <Button size="small" onClick={() => refetch()}>
                  Retry
                </Button>
              </Alert>
            )}
            {!isLoading && groupedEntries.length === 0 && (
              <Typography sx={{ color: "#a8a29e", fontStyle: "italic", py: 4, textAlign: "center" }}>
                {statusFilter !== "all" || filterDateFrom || filterDateTo
                  ? "No entries match these filters."
                  : "Start typing below. Use @ to tag teammates — they'll get a task in My Tasks."}
              </Typography>
            )}
            {!isLoading && personFilter !== "notes" && groupedEntries.length > 0 && visiblePersonGroups.length === 0 && (
              <Typography sx={{ color: "#a8a29e", fontStyle: "italic", py: 4, textAlign: "center" }}>
                {personFilter === "all"
                  ? "No tagged tasks in this filter — notes without an @mention don't have an owner to group by."
                  : "Nothing assigned to this person in the current filter."}
              </Typography>
            )}
            {personFilter !== "notes"
              ? visiblePersonGroups.map((group) => (
                  <Box
                    key={group.name}
                    sx={{
                      mb: 2,
                      bgcolor: "rgba(255,255,255,0.92)",
                      borderRadius: 2,
                      border: "1px solid #e7e5e4",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.6,
                        bgcolor: "#fafaf9",
                        borderBottom: "1px solid #e7e5e4",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.85,
                      }}
                    >
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          bgcolor: avatarColor(group.name),
                          color: "#fff",
                          fontSize: "0.66rem",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {avatarInitial(group.name)}
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: "#292524" }}>
                        {group.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#a8a29e", fontWeight: 600, ml: "auto" }}>
                        {group.openCount} open · {group.totalCount} total
                      </Typography>
                    </Box>
                    <Box sx={{ px: 0.5, py: 0.25 }}>
                      {group.rows.map(({ entry, task, subs }) => (
                        <PersonTaskRow
                          key={`${entry.id}-${task ? task.issue_id : `subs-${group.assigneeId}`}`}
                          entry={entry}
                          task={task}
                          subs={subs}
                          statusUpdatingId={statusUpdatingId}
                          onStatusChange={handleStatusChange}
                          onOpenTask={handleOpenTask}
                          isUpdating={task ? statusUpdatingId === task.issue_id : false}
                          canManageTasks={canManageTasks}
                          inSprintContext={sprintId != null}
                          onMoveToBacklog={handleMoveToBacklog}
                          onMarkNA={handleMarkNA}
                        />
                      ))}
                    </Box>
                  </Box>
                ))
              : groupedEntries.map(([dateKey, entries]) => (
              <Box
                key={dateKey}
                sx={{
                  mb: 2,
                  bgcolor: "rgba(255,255,255,0.92)",
                  borderRadius: 2,
                  border: "1px solid #e7e5e4",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    bgcolor: "#fafaf9",
                    borderBottom: "1px solid #e7e5e4",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      color: "#57534e",
                      fontWeight: 800,
                      letterSpacing: "0.08em",
                      lineHeight: 1.2,
                    }}
                  >
                    {formatDisplayDate(dateKey)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#a8a29e", fontWeight: 600 }}>
                    {entries.length} {entries.length === 1 ? "line" : "lines"}
                  </Typography>
                </Box>
                <Box sx={{ px: 1, py: 0.25 }}>
                  {entries.map((entry) => (
                    <NotebookLine
                      key={entry.id}
                      entry={entry}
                      department={department}
                      canManageTasks={canManageTasks}
                      canDelete={canDelete}
                      canEditLine={canEdit}
                      onDeleteEntry={handleDeleteEntry}
                      onEditEntry={handleEditEntry}
                      onStatusChange={handleStatusChange}
                      onOpenTask={handleOpenTask}
                      onToggleReaction={handleToggleReaction}
                      isReactionPending={reactionMutation.isPending}
                      statusUpdatingId={statusUpdatingId}
                      isDeletingEntry={deletingEntryId === entry.id}
                      isSavingEdit={editingEntryId === entry.id}
                      onMoveToBacklog={handleMoveToBacklog}
                      onMarkNA={handleMarkNA}
                      inSprintContext={sprintId != null}
                    />
                  ))}
                </Box>
              </Box>
                ))}
          </Box>
        </Box>

        {/* ── Sticky composer footer ── */}
        {canEdit && sprintClosed && (
          <Box
            sx={{
              flexShrink: 0,
              borderTop: "2px solid #e7e5e4",
              bgcolor: "#fafaf9",
              px: { xs: 2, md: 3 },
              py: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ color: "#78716c" }}>
              This sprint has shipped — pick a current sprint to add new entries.
            </Typography>
          </Box>
        )}
        {canEdit && !sprintClosed && (
          <Box
            sx={{
              flexShrink: 0,
              borderTop: "2px solid #e7e5e4",
              bgcolor: "#fff",
              boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <Box sx={{ maxWidth: 960, mx: "auto", px: { xs: 2, md: 3 }, py: 1.5, width: "100%" }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.25, alignItems: "center" }}>
                <DatePicker
                  label="Entry date"
                  value={logDate}
                  onChange={(value) => setLogDate(value || dayjs())}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: { width: 150, "& .MuiInputBase-root": { fontSize: "0.82rem" } },
                    },
                  }}
                />
                <DatePicker
                  label="ETA"
                  value={dueDate}
                  onChange={(value) => setDueDate(value || dayjs())}
                  minDate={logDate}
                  slotProps={{
                    textField: {
                      size: "small",
                      required: hasAssignees,
                      error: hasAssignees && !dueDate,
                      sx: { width: 160, "& .MuiInputBase-root": { fontSize: "0.82rem" } },
                    },
                  }}
                />
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  {[0, 1, 2, 3].map((d) => (
                    <Button
                      key={d}
                      size="small"
                      variant={selectedPresetDays === d ? "contained" : "outlined"}
                      onClick={() => applyEtaPreset(d)}
                      sx={{
                        minWidth: 0,
                        px: 1,
                        py: 0.25,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        textTransform: "none",
                        borderColor: "#d6d3d1",
                        color: selectedPresetDays === d ? "#fff" : "#57534e",
                      }}
                    >
                      {d === 0 ? "Today" : `+${d}d`}
                    </Button>
                  ))}
                </Box>
                <Typography variant="caption" sx={{ color: "#78716c", flex: 1, minWidth: 160 }}>
                  {subtaskCount > 0
                    ? `Heading + ${subtaskCount} sub-task${subtaskCount > 1 ? "s" : ""}${
                        assigneeIds.length > 1
                          ? ` · each of the ${assigneeIds.length} tagged people gets their own copy`
                          : ""
                      } · Enter to add`
                    : isMultiLineSingle
                      ? `One task${assigneeIds.length > 1 ? ` for ${assigneeIds.length} people` : ""} · line breaks kept · Enter to add`
                      : "Enter to add · Shift+Enter for new line (name someone on the first line to make the next lines sub-tasks) · @ to tag"}
                  {hasAssignees ? " · ETA syncs to My Tasks due date" : ""}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                <Box sx={{ flex: 1 }}>
                  <MentionComposer
                    value={bodyText}
                    onChange={setBodyText}
                    onSubmit={handleAddLine}
                    members={members}
                    disabled={createMutation.isPending}
                    placeholder="Follow up with @Name on launch checklist…"
                    autoGrow
                  />
                </Box>
                <Button
                  variant="contained"
                  onClick={handleAddLine}
                  disabled={createMutation.isPending || !bodyText.trim()}
                  sx={{ textTransform: "none", fontWeight: 700, px: 2.5, mb: 0.25 }}
                >
                  {createMutation.isPending ? "Adding…" : "Add"}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
        </Box>
      </LocalizationProvider>
    </Dialog>

    {selectedIssueId && selectedIssue
      && String(selectedIssue.issueid) === String(selectedIssueId)
      && (selectedIssue.issuetype === "Story" || selectedIssue.issuetype === "Task") ? (
        <TaskDetailModal
          task={selectedIssue}
          onClose={handleCloseTaskDetail}
          navigationIds={modalNavIds ?? notebookNavIds}
          onNavigate={(id) => setSelectedIssueId(String(id))}
          onStatusChange={(issueId, status) => {
            handleStatusChange(issueId, status);
            handleTaskDetailUpdated();
          }}
          onReassign={handleReassignTask}
          users={members}
          projectId={projectId}
          sprintId={sprintId}
          canEdit={canManageTasks}
          canDelete={canDelete}
          onDeleteIssue={canDelete ? handleDeleteIssueFromDetail : undefined}
          onTaskCreated={handleTaskDetailUpdated}
        />
      ) : selectedIssueId && selectedIssue ? (
        <IssueDetailModal
          issueId={selectedIssueId}
          users={members}
          onClose={handleCloseTaskDetail}
          onStatusChanged={handleTaskDetailUpdated}
          onIssueUpdated={handleTaskDetailUpdated}
        />
      ) : null}
    </>
  );
}
